import { useState, FormEvent } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9ostuqqs38sj6jc7eu0';
const SITE_URL = 'https://www.krcomic.com';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();

    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    try {
      const formData = new FormData(form);
      formData.delete('website_alt');
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      const code = (parsed?.code as string) || '';
      const serverMsg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫大全',
    url: SITE_URL,
    description: '最新最全的正版韩国漫画在线阅读平台',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@krcomic.com',
    },
  };

  const milestones = [
    { year: '2020', title: '平台创立', desc: '韩漫大全正式上线，致力于为中文读者提供正版韩国漫画' },
    { year: '2021', title: '内容扩充', desc: '漫画作品突破50部，涵盖5大题材分类，日活用户突破10万' },
    { year: '2022', title: '品质升级', desc: '全面升级高清画质，优化移动端阅读体验，用户满意度大幅提升' },
    { year: '2023', title: '生态拓展', desc: '与多家韩国漫画平台建立深度合作，日更新量翻倍增长' },
    { year: '2024', title: '行业领先', desc: '成为中文韩漫阅读领域的标杆平台，服务超过百万忠实读者' },
  ];

  const testimonials = [
    { name: '漫画迷小王', text: '韩漫大全的更新速度真的很快，画质也非常清晰，是我每天必刷的网站！', rating: 5 },
    { name: '阅读爱好者', text: '分类很齐全，找漫画特别方便。而且全部免费正版，体验非常好。', rating: 5 },
    { name: '资深韩漫粉', text: '用了很多韩漫平台，韩漫大全是最让我满意的。界面清爽，阅读流畅，强烈推荐！', rating: 5 },
    { name: '新入坑读者', text: '作为一个刚开始看韩漫的新人，这个网站的分类和推荐功能帮我快速找到了喜欢的作品。', rating: 4 },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 韩漫大全 | 品牌介绍、口碑与联系方式"
        description="韩漫大全致力于为中文读者提供最新最全的正版韩国漫画在线阅读服务。了解我们的品牌故事、发展历程、用户口碑，以及联系方式。正版授权，高清画质，每日更新。"
        keywords="关于我们,韩漫大全,品牌介绍,联系方式,用户口碑,韩国漫画"
        canonicalUrl={`${SITE_URL}/about`}
        schema={orgSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-16 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

          <section className="mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-6">关于韩漫大全</h1>
            <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                <strong>韩漫大全</strong>成立于2020年，是国内领先的正版韩国漫画在线阅读平台。我们致力于为中文读者提供最新、最全、最高清的韩国漫画资源，让每一位韩漫爱好者都能享受到流畅舒适的阅读体验。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                我们与多家韩国顶级漫画平台和出版社建立了长期合作关系，确保所有内容均为正版授权。平台涵盖<strong>热血动作、奇幻冒险、浪漫恋爱、悬疑惊悚</strong>等多种题材，满足不同读者的阅读偏好。
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                作为一个以用户体验为核心的产品，韩漫大全始终坚持<strong>免费阅读、高清画质、每日更新</strong>的承诺。我们相信，优质的漫画内容应该被更多人欣赏，正版阅读是支持创作者最好的方式。
              </p>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">发展历程</h2>
            <div className="relative">
              <div className="absolute left-[19px] top-2 bottom-2 w-0.5 bg-accent-200 hidden md:block" />
              {milestones.map((m, i) => (
                <div key={m.year} className="flex gap-6 mb-8 last:mb-0">
                  <div className="hidden md:flex items-start pt-1">
                    <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-background-50">{m.year.slice(2)}</span>
                    </div>
                  </div>
                  <div className="flex-1 bg-background-100 rounded-xl p-5 border border-background-200/70 md:ml-0">
                    <div className="md:hidden text-xs text-accent-600 font-bold mb-1">{m.year}</div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-1">{m.title}</h3>
                    <p className="text-sm text-foreground-600">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">用户口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
              {testimonials.map((t, i) => (
                <div key={i} className="bg-background-100 rounded-xl p-5 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, j) => (
                      <div key={j} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-500 text-sm" />
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3">&ldquo;{t.text}&rdquo;</p>
                  <p className="text-xs text-foreground-500 font-medium">—— {t.name}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="contact" className="mb-8">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-8">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-500">contact@krcomic.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-customer-service-line text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服热线</h3>
                    <p className="text-sm text-foreground-500">400-888-6666</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-secondary-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h3>
                    <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                  </div>
                </div>
              </div>

              <div>
                <form onSubmit={handleSubmit} className="bg-background-100 rounded-xl p-5 md:p-6 border border-background-200/70" data-readdy-form>
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">发送消息</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        maxLength={500}
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息已发送成功，我们会尽快回复您！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}